import { Box, Text } from '@mantine/core'
import { type NavigateOptions, type To } from 'react-router-dom'

import { SeeAllBtn } from '../../../common/components/SeeAllBtn/SeeAllBtn'

import classes from './SuggestedRow.module.scss'
import { MultiSlideCarousel } from '@percent/lemonade'
import { useIsMobile } from '@percent/workplace-giving/common/hooks'

export type SuggestedRowProps<T> = Readonly<{
  title: string
  seeAllHref?: To
  items: T[]
  keyExtractor: (item: T) => string
  renderItem: (item: T) => JSX.Element
  testId?: string
  seeAllNavigateOptions?: NavigateOptions
}>

export function SuggestedRow<T>({
  title,
  seeAllHref,
  seeAllNavigateOptions,
  items,
  keyExtractor,
  renderItem,
  testId
}: SuggestedRowProps<T>): JSX.Element {
  const isMobile = useIsMobile()

  // On mobile the cards would otherwise stack one per row, making the page very
  // long — a carousel keeps the section to a single card's height.
  if (isMobile) {
    return (
      <Box className={classes.SuggestedRowContainer} data-testid={testId}>
        <MultiSlideCarousel
          title={title}
          description={
            seeAllHref ? (
              <Box className={classes.SuggestedRowSeeAll}>
                <SeeAllBtn href={seeAllHref} navigateOptions={seeAllNavigateOptions} />
              </Box>
            ) : undefined
          }
          headerClassName={classes.SuggestedRowCarouselHeader}
          titleClassName={classes.SuggestedRowCarouselTitle}
          navWrapperClassName={classes.SuggestedRowNav}
          itemsPerPage={{ xs: 1.2 }}
          slideGap="1rem"
          items={items.map(item => ({ id: keyExtractor(item), content: renderItem(item) }))}
        />
      </Box>
    )
  }

  return (
    <Box className={classes.SuggestedRowContainer} data-testid={testId}>
      <Box className={classes.SuggestedRowHeader}>
        <Text component="h2" className={classes.SuggestedRowTitle}>
          {title}
        </Text>
        {seeAllHref && <SeeAllBtn href={seeAllHref} navigateOptions={seeAllNavigateOptions} />}
      </Box>
      <Box className={classes.SuggestedRowContent}>
        {items.map(item => (
          <Box key={keyExtractor(item)} className={classes.SuggestedRowItem}>
            {renderItem(item)}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
